// The signs Sing in Solfège's staff writes besides its notes (issue #3772): a sharp, flat or natural
// on a chromatic note, and for a minor phrase the singer's clef and the key signature of do's minor
// key at the left of the staff. Pure numbers and outlines - no React, no DOM - like the rest of the
// staff's geometry, which places them (solfege_staff_geometry.ts).
//
// What is written (the product owner, 2026-10-03). A major phrase keeps the plain staff the module
// has always drawn: no clef, no key signature, its lines and spaces read as do's major scale wherever
// do sits, so a chromatic note's sign says only which way it leaves its line's note - a sharp for a
// raised note (fi on fa's line), a flat for a lowered one (te on ti's). A minor phrase is drawn as
// printed music draws it: the clef the staff already places notes by, and the key signature of do's
// minor key (C minor's three flats, F minor's four, G minor's two), so its own me, le and te need no
// mark and a raised la or ti takes the sign real notation gives that pitch under that signature: a
// natural where the signature flattens the note's letter (B natural for ti in C minor), a sharp where
// it leaves the letter alone (F sharp for ti in G minor). Nothing is looked up by key: the signature
// is the alterations do's minor scale puts on the staff's letters, and a note's sign compares the
// alteration its pitch puts on its own letter with the signature's.
//
// The shapes. Each sign is drawn as an engraver draws it, with thick and thin strokes: a curved
// stroke is a centre line through a few points with a width at each, its outline the line offset by
// half the width either side, so the treble clef's diagonal and the bass clef's body swell and the
// joins and hairlines thin, as in printed music. Straight strokes (a sharp's or a natural's stems
// and bars) are plain polygons. Every shape is in staff spaces, y downwards, filled by the nonzero
// rule, with its pieces' convex hulls kept for the clearances the spacing works out.

import { Clef } from '../../../../../common/src/learn_rules.ts';
import {
  isSolfegeChromatic,
  SolfegeMode,
  SolfegeNote,
  solfegeNoteMidi,
  solfegeScaleAlteration,
} from '../../../../../common/src/sing_in_solfege.ts';
import { midiForStaffStep, stepLetter } from '../pitch/staff_geometry.ts';
import type { SolfegeBox, SolfegePoint } from './solfege_staff_geometry.ts';

// ---- What is written -------------------------------------------------------------------------

export type SolfegeAccidental = 'sharp' | 'flat' | 'natural';

// One accidental of a key signature, on the line or space its clef puts it on
export interface SolfegeKeySignatureSign {
  letter: string;
  step: number;
  accidental: 'sharp' | 'flat';
}

export interface SolfegeKeySignature {
  // How the signature alters each letter it names, in semitones; a letter it does not name is natural
  alterations: Record<string, number>;
  signs: SolfegeKeySignatureSign[]; // in the order they are written, left to right
}

// The order a key signature writes its flats and its sharps
const flatOrder = 'BEADGCF';
const sharpOrder = 'FCGDAEB';

// Where a signature's accidentals stand, as printed music has them: each flat on the step for its
// letter from the clef's lowest F up (treble F4 to E5, bass F2 to E3), so they zigzag B E A D G C F
// down and up the staff; each sharp from the clef's lowest A up (treble A4 to G5, bass A2 to G3)
const flatRangeStart: Record<Clef, number> = { treble: 1, bass: -1 };
const sharpRangeStart: Record<Clef, number> = { treble: 3, bass: 1 };

// How far a staff step's letter is moved to sound `midi`: 0 for the natural, 1 sharp, -1 flat
function letterAlteration(midi: number, step: number, clef: Clef): number {
  return midi - midiForStaffStep(step, clef);
}

// The key signature a minor phrase is written with: do's minor key, for this placement of do in
// this clef. A major phrase has none, as the plain staff draws it.
export function solfegeKeySignature({
  mode,
  homeMidi,
  doStep,
  clef,
}: {
  mode: SolfegeMode;
  homeMidi: number;
  doStep: number;
  clef: Clef;
}): SolfegeKeySignature | undefined {
  if (mode === 'major') {
    return undefined;
  }
  const alterations: Record<string, number> = {};
  for (let degree = 0; degree < 7; degree += 1) {
    const midi = solfegeNoteMidi(homeMidi, { degree, alteration: solfegeScaleAlteration(mode, degree) });
    const alteration = letterAlteration(midi, doStep + degree, clef);
    if (alteration !== 0) {
      alterations[stepLetter(doStep + degree, clef)] = alteration;
    }
  }
  const signsOf = (accidental: 'sharp' | 'flat', order: string, rangeStart: number): SolfegeKeySignatureSign[] =>
    [...order]
      .filter((letter) => alterations[letter] === (accidental === 'flat' ? -1 : 1))
      .map((letter) => {
        const offset = Array.from({ length: 7 }, (_unused, index) => index).find(
          (index) => stepLetter(rangeStart + index, clef) === letter,
        ) as number;
        return { letter, step: rangeStart + offset, accidental };
      });
  return {
    alterations,
    signs: [
      ...signsOf('flat', flatOrder, flatRangeStart[clef]),
      ...signsOf('sharp', sharpOrder, sharpRangeStart[clef]),
    ],
  };
}

// The sign a note carries, or none: only a note chromatic for its phrase's mode carries one. On the
// plain staff of a major phrase, the way it leaves its line's note; under a key signature, the
// alteration its pitch puts on its letter, written as a sharp, a natural or a flat, which always
// differs from the signature's for a chromatic note (see the header).
export function solfegeNoteAccidental({
  note,
  mode,
  homeMidi,
  step,
  clef,
  signature,
}: {
  note: Pick<SolfegeNote, 'degree' | 'alteration'>;
  mode: SolfegeMode;
  homeMidi: number;
  step: number;
  clef: Clef;
  signature?: SolfegeKeySignature;
}): SolfegeAccidental | undefined {
  if (!isSolfegeChromatic(note, mode)) {
    return undefined;
  }
  if (!signature) {
    return (note.alteration ?? 0) > 0 ? 'sharp' : 'flat';
  }
  const spelled = letterAlteration(solfegeNoteMidi(homeMidi, note), step, clef);
  if (spelled === (signature.alterations[stepLetter(step, clef)] ?? 0)) {
    return undefined;
  }
  return spelled > 0 ? 'sharp' : spelled < 0 ? 'flat' : 'natural';
}

// ---- The shapes ------------------------------------------------------------------------------

export type SolfegeSignShapeName = SolfegeAccidental | 'trebleClef' | 'bassClef';

export interface SolfegeSignShape {
  d: string; // the outline, in staff spaces from the shape's origin, y downwards, nonzero fill
  balls: { cx: number; cy: number; r: number }[]; // filled circles: a clef's ball, the bass clef's dots
  parts: SolfegePoint[][]; // convex pieces that together cover the shape, for the clearances
  box: SolfegeBox;
}

type Point = [number, number];
// A point of a stroke's centre line, y upwards as the shapes are designed, and its width there
type CentrePoint = [number, number, number];

const strokeSamples = 12;

// A stroke of varying width as a closed outline, y downwards: the centre line a Catmull-Rom curve
// through the points (the width interpolated along it the same way), offset half the width to
// either side, out along one side and back along the other
function strokeOutline(centre: CentrePoint[]): Point[] {
  const count = centre.length;
  const at = (index: number) => centre[Math.max(0, Math.min(count - 1, index))];
  const left: Point[] = [];
  const right: Point[] = [];
  const add = (x: number, y: number, width: number, dx: number, dy: number) => {
    const length = Math.hypot(dx, dy) || 1;
    const nx = -dy / length;
    const ny = dx / length;
    left.push([x + (nx * width) / 2, -(y + (ny * width) / 2)]);
    right.push([x - (nx * width) / 2, -(y - (ny * width) / 2)]);
  };
  for (let index = 0; index < count - 1; index += 1) {
    const [p0, p1, p2, p3] = [at(index - 1), at(index), at(index + 1), at(index + 2)];
    for (let sample = 0; sample < strokeSamples; sample += 1) {
      const t = sample / strokeSamples;
      const curve = (axis: number) =>
        0.5 *
        (2 * p1[axis] +
          (p2[axis] - p0[axis]) * t +
          (2 * p0[axis] - 5 * p1[axis] + 4 * p2[axis] - p3[axis]) * t * t +
          (3 * p1[axis] - p0[axis] - 3 * p2[axis] + p3[axis]) * t * t * t);
      const slope = (axis: number) =>
        0.5 *
        (p2[axis] -
          p0[axis] +
          2 * (2 * p0[axis] - 5 * p1[axis] + 4 * p2[axis] - p3[axis]) * t +
          3 * (3 * p1[axis] - p0[axis] - 3 * p2[axis] + p3[axis]) * t * t);
      add(curve(0), curve(1), curve(2), slope(0), slope(1));
    }
  }
  const [last, before] = [centre[count - 1], centre[count - 2]];
  add(last[0], last[1], last[2], last[0] - before[0], last[1] - before[1]);
  return [...left, ...right.reverse()];
}

// A polygon designed with y upwards, turned y downwards
function flipped(points: Point[]): Point[] {
  return points.map(([x, y]) => [x, -y]);
}

// The same polygon wound the way every other piece is, so overlapping pieces fill under the nonzero
// rule rather than cancelling where they cross
function wound(points: Point[]): Point[] {
  const area = points.reduce((sum, [x, y], index) => {
    const [nextX, nextY] = points[(index + 1) % points.length];
    return sum + x * nextY - nextX * y;
  }, 0);
  return area < 0 ? [...points].reverse() : points;
}

// A straight bar of an accidental, y upwards: from x1 to x2, its centre line through (middle, y) at
// `slope`, `thickness` tall
function bar(x1: number, x2: number, y: number, slope: number, thickness: number): Point[] {
  const middle = (x1 + x2) / 2;
  const centre = (x: number) => y + slope * (x - middle);
  return [
    [x1, centre(x1) - thickness / 2],
    [x2, centre(x2) - thickness / 2],
    [x2, centre(x2) + thickness / 2],
    [x1, centre(x1) + thickness / 2],
  ];
}

function rect(x1: number, x2: number, y1: number, y2: number): Point[] {
  return [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ];
}

// The convex hull of points (Andrew's monotone chain)
function hull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point, a: Point, b: Point) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (from: Point[]) => {
    const chain: Point[] = [];
    for (const point of from) {
      while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], point) <= 0) {
        chain.pop();
      }
      chain.push(point);
    }
    chain.pop();
    return chain;
  };
  return [...half(sorted), ...half([...sorted].reverse())];
}

function pathOf(polygons: Point[][]): string {
  const number = (value: number) => String(Math.round(value * 1000) / 1000);
  return polygons.map((polygon) => `M${polygon.map(([x, y]) => `${number(x)} ${number(y)}`).join('L')}Z`).join('');
}

// A shape from its filled pieces (y downwards) and its balls, moved so its origin is where the
// staff places it from
function shapeOf(pieces: Point[][], balls: [number, number, number][], [originX, originY]: Point): SolfegeSignShape {
  const moved = pieces.map((piece) => wound(piece.map(([x, y]): Point => [x - originX, y - originY])));
  const ballShapes = balls.map(([cx, cy, r]) => ({ cx: cx - originX, cy: cy - originY, r }));
  // Each ball's hull is a polygon around it, close enough to a circle for a clearance
  const ballParts = ballShapes.map(({ cx, cy, r }) =>
    Array.from({ length: 16 }, (_unused, index): Point => {
      const angle = (index * Math.PI) / 8;
      // the polygon's sides touch the circle, so it holds the whole ball
      const out = r / Math.cos(Math.PI / 16);
      return [cx + out * Math.cos(angle), cy + out * Math.sin(angle)];
    }),
  );
  const parts = [...moved.map(hull), ...ballParts];
  const all = parts.flat();
  return {
    d: pathOf(moved),
    balls: ballShapes,
    parts: parts.map((part) => part.map(([x, y]) => ({ x, y }))),
    box: {
      x1: Math.min(...all.map(([x]) => x)),
      y1: Math.min(...all.map(([, y]) => y)),
      x2: Math.max(...all.map(([x]) => x)),
      y2: Math.max(...all.map(([, y]) => y)),
    },
  };
}

// The treble clef, its curl on the G line (the origin's y) and its left edge at the origin's x: one
// stroke from the curl round the body, up the swelling diagonal to the top loop and down the thin
// stem to the hook and its ball, two staff spaces and two thirds wide and seven tall
const trebleClefStroke: CentrePoint[] = [
  [1.28, -0.4, 0.1],
  [0.98, -0.3, 0.16],
  [0.8, 0.1, 0.22],
  [0.98, 0.55, 0.2],
  [1.55, 0.84, 0.13],
  [2.2, 0.68, 0.24],
  [2.55, 0.12, 0.34],
  [2.32, -0.58, 0.28],
  [1.6, -0.97, 0.13],
  [0.78, -0.8, 0.22],
  [0.22, -0.12, 0.36],
  [0.16, 0.5, 0.4],
  [0.6, 1.3, 0.43],
  [1.3, 2.0, 0.42],
  [1.95, 2.8, 0.34],
  [2.17, 3.55, 0.24],
  [2.0, 4.18, 0.17],
  [1.66, 4.36, 0.14],
  [1.36, 3.95, 0.14],
  [1.24, 3.0, 0.15],
  [1.48, 1.7, 0.16],
  [1.74, 0.2, 0.16],
  [1.9, -1.3, 0.16],
  [1.86, -2.12, 0.17],
  [1.55, -2.45, 0.2],
  [1.12, -2.22, 0.22],
];

// The bass clef, its ball on the F line (the origin's y) and its left edge at the origin's x: the
// ball, the body swelling round to the right and tapering to a point below, and the two dots either
// side of the F line
const bassClefStroke: CentrePoint[] = [
  [0.55, 0.22, 0.34],
  [0.85, 0.7, 0.22],
  [1.45, 0.86, 0.22],
  [1.98, 0.5, 0.36],
  [2.14, -0.15, 0.43],
  [1.96, -0.88, 0.4],
  [1.45, -1.58, 0.3],
  [0.85, -2.02, 0.17],
  [0.2, -2.32, 0.05],
];

// A flat's bowl, from the stem round to the right and back to the stem's foot in a point, thin at the
// top and thick on its right as an engraved flat's is
const flatBowlStroke: CentrePoint[] = [
  [0.1, 0.24, 0.08],
  [0.36, 0.5, 0.13],
  [0.68, 0.44, 0.23],
  [0.8, 0.08, 0.3],
  [0.56, -0.3, 0.22],
  [0.12, -0.58, 0.07],
];

// Accidentals stand with their origin at their right edge and on the note's line or space, so the
// staff can stand one just left of a head
const sharpWidth = 1.02;
const naturalWidth = 0.68;
const flatWidth = 0.95;

const accidentalSlope = 0.28; // how much a sharp's or natural's bars rise across, per staff space

export const solfegeSignShapes: Record<SolfegeSignShapeName, SolfegeSignShape> = {
  // Two thin stems, the right one higher, and two thick bars rising to the right
  sharp: shapeOf(
    [
      rect(0.26, 0.41, -1.4, 1.18),
      rect(0.61, 0.76, -1.18, 1.4),
      bar(0, sharpWidth, 0.5, accidentalSlope, 0.42),
      bar(0, sharpWidth, -0.5, accidentalSlope, 0.42),
    ].map(flipped),
    [],
    [sharpWidth, 0],
  ),
  // The left stem rising above the bars, the right one falling below them
  natural: shapeOf(
    [
      rect(0, 0.15, -0.42 - accidentalSlope * 0.34 - 0.2, 1.35),
      rect(naturalWidth - 0.15, naturalWidth, -1.35, 0.42 + accidentalSlope * 0.34 + 0.2),
      bar(0, naturalWidth, 0.42, accidentalSlope, 0.4),
      bar(0, naturalWidth, -0.42, accidentalSlope, 0.4),
    ].map(flipped),
    [],
    [naturalWidth, 0],
  ),
  // A tall thin stem and its bowl
  flat: shapeOf([flipped(rect(0, 0.15, -0.62, 1.75)), strokeOutline(flatBowlStroke)], [], [flatWidth, 0]),
  trebleClef: shapeOf([strokeOutline(trebleClefStroke)], [[1.02, 1.96, 0.42]], [0, 0]),
  bassClef: shapeOf(
    [strokeOutline(bassClefStroke)],
    [
      [0.5, 0, 0.45],
      [2.62, -0.5, 0.2],
      [2.62, 0.5, 0.2],
    ],
    [0, 0],
  ),
};

// ---- A sign on the staff ---------------------------------------------------------------------

// A shape drawn at (x, y), `unit` viewBox units to its staff space: where it is, its box and its
// convex pieces in the viewBox's units, and the transform that draws it
export interface SolfegeSign {
  shape: SolfegeSignShapeName;
  x: number;
  y: number;
  unit: number;
  box: SolfegeBox;
  parts: SolfegePoint[][];
}

export function solfegeSign(shape: SolfegeSignShapeName, x: number, y: number, unit: number): SolfegeSign {
  const { box, parts } = solfegeSignShapes[shape];
  return {
    shape,
    x,
    y,
    unit,
    box: { x1: x + unit * box.x1, y1: y + unit * box.y1, x2: x + unit * box.x2, y2: y + unit * box.y2 },
    parts: parts.map((part) => part.map((point) => ({ x: x + unit * point.x, y: y + unit * point.y }))),
  };
}

export function solfegeSignTransform(sign: SolfegeSign): string {
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return `translate(${round(sign.x)} ${round(sign.y)}) scale(${round(sign.unit)})`;
}
